import React, { useEffect, useState } from 'react';
import * as S from '../Advances/AdvancesStyles';
import {
  finalizeDetailedSalarySlip,
  getDetailedSalarySlip,
} from '../../../../services/attendanceService';
import { toast } from 'react-toastify';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmt = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function numberToWordsINR(num) {
  const n = Math.round(Number(num) || 0);
  if (n === 0) return 'Rupees Zero Only';
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const b = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
  ];

  function inWords(val) {
    if (val === 0) return '';
    if (val < 20) return a[val] + ' ';
    if (val < 100) return b[Math.floor(val / 10)] + ' ' + (a[val % 10] ? a[val % 10] + ' ' : '');
    if (val < 1000) return inWords(Math.floor(val / 100)) + 'Hundred ' + inWords(val % 100);
    if (val < 100000) return inWords(Math.floor(val / 1000)) + 'Thousand ' + inWords(val % 1000);
    if (val < 10000000) return inWords(Math.floor(val / 100000)) + 'Lakh ' + inWords(val % 100000);
    return inWords(Math.floor(val / 10000000)) + 'Crore ' + inWords(val % 10000000);
  }
  return `Rupees ${inWords(n).trim().replace(/\s+/g, ' ')} Only`;
}

export function computeLivePayslip({
  monthly_ctc = 50000,
  pf_applicable = true,
  esic_applicable = true,
  eps_member = true,
  total_days = 31,
  lop_days = 0,
  other_earning = 0,
  salary_advance = 0,
  loan_recovery = 0,
  tds = 0,
  reimbursement = 0,
}) {
  const ctc = Math.max(0, Number(monthly_ctc) || 0);
  const basic = parseFloat((ctc * 0.5).toFixed(2));
  const hra = parseFloat((basic * 0.5).toFixed(2));
  const statutory_bonus =
    basic <= 21000 ? parseFloat((basic * 0.0833).toFixed(2)) : 0;
  const pf_wage_full = pf_applicable ? Math.min(basic, 15000) : 0;
  const employer_pf_total_full = pf_applicable
    ? parseFloat((pf_wage_full * 0.13).toFixed(2))
    : 0;
  const special_allowance = Math.max(
    0,
    parseFloat(
      (ctc - basic - hra - statutory_bonus - employer_pf_total_full).toFixed(2)
    )
  );
  const gross_full = parseFloat(
    (basic + hra + statutory_bonus + special_allowance).toFixed(2)
  );

  const totalDaysNum = Number(total_days) || 30;
  const lopNum = Number(lop_days) || 0;
  const paid_days = Math.max(0, Math.min(totalDaysNum, totalDaysNum - lopNum));
  const proration = totalDaysNum > 0 ? paid_days / totalDaysNum : 0;

  const earned_basic = parseFloat((basic * proration).toFixed(2));
  const earned_hra = parseFloat((hra * proration).toFixed(2));
  const earned_bonus = parseFloat((statutory_bonus * proration).toFixed(2));
  const earned_special_allowance = parseFloat(
    (special_allowance * proration).toFixed(2)
  );
  const otherEarnNum = Number(other_earning) || 0;
  const gross_earned = parseFloat(
    (
      earned_basic +
      earned_hra +
      earned_bonus +
      earned_special_allowance +
      otherEarnNum
    ).toFixed(2)
  );

  // PF Deductions
  const pf_wage = pf_applicable ? Math.min(earned_basic, 15000) : 0;
  const employee_pf = pf_applicable ? Math.round(0.12 * pf_wage) : 0;
  const eps = pf_applicable && eps_member ? Math.round(0.0833 * pf_wage) : 0;
  const employer_epf = pf_applicable ? Math.max(0, employee_pf - eps) : 0;
  const admin_charges = pf_applicable ? Math.round(0.005 * pf_wage) : 0;
  const edli = pf_applicable ? Math.round(0.005 * pf_wage) : 0;
  const employer_pf_total = eps + employer_epf + admin_charges + edli;

  // ESIC Deductions (eligible if basic <= 21000)
  const is_esic_eligible = esic_applicable && basic <= 21000;
  const esic_wage = is_esic_eligible ? earned_basic : 0;
  const employee_esic = is_esic_eligible ? Math.ceil(0.0075 * esic_wage) : 0;
  const employer_esic = is_esic_eligible
    ? parseFloat((0.0325 * esic_wage).toFixed(2))
    : 0;

  // Deductions
  const loanRecNum = Number(loan_recovery) || 0;
  const salAdvNum = Number(salary_advance) || 0;
  const tdsNum = Number(tds) || 0;

  const total_deductions = parseFloat(
    (
      employee_pf +
      employee_esic +
      loanRecNum +
      salAdvNum +
      tdsNum
    ).toFixed(2)
  );

  const net_take_home = Math.max(
    0,
    parseFloat((gross_earned - total_deductions).toFixed(2))
  );
  const reimbNum = Number(reimbursement) || 0;
  const final_take_home = parseFloat((net_take_home + reimbNum).toFixed(2));

  return {
    monthly_ctc: ctc,
    basic,
    hra,
    statutory_bonus,
    special_allowance,
    gross_full,
    paid_days,
    lop_days: lopNum,
    total_days: totalDaysNum,
    earned_basic,
    earned_hra,
    earned_bonus,
    earned_special_allowance,
    other_earning: otherEarnNum,
    gross_earned,
    pf_applicable,
    pf_wage,
    employee_pf,
    eps,
    employer_epf,
    admin_charges,
    edli,
    employer_pf_total,
    esic_applicable: is_esic_eligible,
    esic_wage,
    employee_esic,
    employer_esic,
    loan_recovery: loanRecNum,
    salary_advance: salAdvNum,
    tds: tdsNum,
    total_deductions,
    net_take_home,
    reimbursement: reimbNum,
    final_take_home,
  };
}

const PayslipReviewModal = ({
  show,
  userId,
  userName,
  employeeCode,
  month,
  year,
  initialPayslip,
  onClose,
  onFinalized,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalized, setFinalized] = useState(false);

  // Form State
  const [monthlyCtc, setMonthlyCtc] = useState(50000);
  const [pfApplicable, setPfApplicable] = useState(true);
  const [esicApplicable, setEsicApplicable] = useState(true);
  const [epsMember, setEpsMember] = useState(true);
  const [totalDays, setTotalDays] = useState(31);
  const [lopDays, setLopDays] = useState(0);
  const [otherEarning, setOtherEarning] = useState(0);
  const [salaryAdvance, setSalaryAdvance] = useState(0);
  const [loanRecovery, setLoanRecovery] = useState(0);
  const [tds, setTds] = useState(0);
  const [reimbursement, setReimbursement] = useState(0);
  const [empCode, setEmpCode] = useState(employeeCode || '');

  // Initialize or fetch detailed slip
  useEffect(() => {
    if (!show || !userId) return;

    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getDetailedSalarySlip(userId, month, year);
        if (!isMounted) return;

        const slip = res?.payslip || initialPayslip;
        const master = res?.master || {};
        const input = res?.monthly_input || {};

        if (slip) {
          setMonthlyCtc(slip.monthly_ctc || master.monthly_ctc || 50000);
          setPfApplicable(
            slip.pf_applicable !== undefined
              ? Boolean(slip.pf_applicable)
              : master.pf_applicable !== undefined
              ? Boolean(master.pf_applicable)
              : true
          );
          setEsicApplicable(
            master.esic_applicable !== undefined
              ? Boolean(master.esic_applicable)
              : true
          );
          setEpsMember(
            master.eps_member !== undefined ? Boolean(master.eps_member) : true
          );
          setTotalDays(
            slip.total_days_in_month ||
              new Date(year, month, 0).getDate() ||
              31
          );
          setLopDays(
            slip.lop_days !== undefined
              ? slip.lop_days
              : input.lop_days !== undefined
              ? input.lop_days
              : 0
          );
          setOtherEarning(slip.other_earning || input.other_earning || 0);
          setSalaryAdvance(slip.salary_advance || input.salary_advance || 0);
          setLoanRecovery(slip.loan_recovery || input.loan_recovery || 0);
          setTds(slip.tds || input.tds || 0);
          setReimbursement(slip.reimbursement || input.reimbursement || 0);
          setEmpCode(slip.employee_code || master.employee_code || employeeCode || '');
          setFinalized(res?.is_finalized || slip.status === 'GENERATED' || slip.status === 'PAID');
        }
      } catch (e) {
        console.error('Error fetching detailed slip in modal:', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [show, userId, month, year, initialPayslip, employeeCode]);

  if (!show) return null;

  // Live calculation
  const calc = computeLivePayslip({
    monthly_ctc: monthlyCtc,
    pf_applicable: pfApplicable,
    esic_applicable: esicApplicable,
    eps_member: epsMember,
    total_days: totalDays,
    lop_days: lopDays,
    other_earning: otherEarning,
    salary_advance: salaryAdvance,
    loan_recovery: loanRecovery,
    tds: tds,
    reimbursement: reimbursement,
  });

  const handleFinalize = async () => {
    setSaving(true);
    try {
      const payload = {
        user_id: userId,
        month,
        year,
        employee_code: empCode,
        structure_type: 'Standard',
        total_days_in_month: calc.total_days,
        paid_days: calc.paid_days,
        lop_days: calc.lop_days,
        monthly_ctc: calc.monthly_ctc,
        basic: calc.basic,
        hra: calc.hra,
        statutory_bonus: calc.statutory_bonus,
        special_allowance: calc.special_allowance,
        gross_full: calc.gross_full,
        earned_basic: calc.earned_basic,
        earned_hra: calc.earned_hra,
        earned_bonus: calc.earned_bonus,
        earned_special_allowance: calc.earned_special_allowance,
        other_earning: calc.other_earning,
        gross_earned: calc.gross_earned,
        pf_applicable: calc.pf_applicable,
        pf_wage: calc.pf_wage,
        employee_pf: calc.employee_pf,
        eps: calc.eps,
        employer_epf: calc.employer_epf,
        admin_charges: calc.admin_charges,
        edli: calc.edli,
        employer_pf_total: calc.employer_pf_total,
        esic_applicable: calc.esic_applicable,
        esic_wage: calc.esic_wage,
        employee_esic: calc.employee_esic,
        employer_esic: calc.employer_esic,
        loan_recovery: calc.loan_recovery,
        salary_advance: calc.salary_advance,
        tds: calc.tds,
        total_deductions: calc.total_deductions,
        net_take_home: calc.net_take_home,
        reimbursement: calc.reimbursement,
        final_take_home: calc.final_take_home,
      };

      await finalizeDetailedSalarySlip(payload);
      setFinalized(true);
      toast.success('Payslip finalized and saved successfully!');
      if (onFinalized) onFinalized(payload);
    } catch (err) {
      console.error('Finalize detailed slip failed:', err);
      toast.error('Failed to finalize payslip.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCsv = () => {
    const header = [
      'Field',
      'Full Rate (₹)',
      'Earned / Deducted (₹)',
    ];
    const rows = [
      ['Employee Name', userName, ''],
      ['Employee Code', empCode, ''],
      ['Month / Year', `${MONTHS[month - 1]} ${year}`, ''],
      ['Total Days in Month', calc.total_days, ''],
      ['Days Worked (Paid)', calc.paid_days, ''],
      ['LOP / Absent Days', calc.lop_days, ''],
      ['--- EARNINGS ---', '', ''],
      ['Basic Salary', calc.basic, calc.earned_basic],
      ['House Rent Allowance (HRA)', calc.hra, calc.earned_hra],
      ['Statutory Bonus', calc.statutory_bonus, calc.earned_bonus],
      ['Special Allowance', calc.special_allowance, calc.earned_special_allowance],
      ['Other Earnings', '', calc.other_earning],
      ['GROSS EARNINGS', calc.gross_full, calc.gross_earned],
      ['--- DEDUCTIONS ---', '', ''],
      ['Employee PF (12%)', '', calc.employee_pf],
      ['Employee ESIC (0.75%)', '', calc.employee_esic],
      ['Salary Advance Recovery', '', calc.salary_advance],
      ['Loan Recovery', '', calc.loan_recovery],
      ['TDS / Income Tax', '', calc.tds],
      ['GROSS DEDUCTIONS', '', calc.total_deductions],
      ['--- NET TAKE HOME ---', '', ''],
      ['Net Salary', '', calc.net_take_home],
      ['Reimbursements', '', calc.reimbursement],
      ['FINAL TAKE HOME', '', calc.final_take_home],
      ['In Words', numberToWordsINR(calc.final_take_home), ''],
    ];

    const csvContent = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${userName.replace(/\s+/g, '_')}_${MONTHS[month - 1]}_${year}_TeamNest_Payslip.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <S.ModalOverlay show={show} onClick={onClose} style={{ zIndex: 1050 }}>
      <S.ModalContent
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 960,
          width: '95%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '24px 28px',
          borderRadius: 14,
          background: '#ffffff',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: '1px solid #E2E8F0',
            paddingBottom: 14,
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1E293B' }}>
                {userName}
              </h2>
              <span
                style={{
                  background: '#EEF2FF',
                  color: '#4F46E5',
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                }}
              >
                {empCode || 'STAFF'}
              </span>
              <span
                style={{
                  background: finalized ? '#ECFDF5' : '#FFFBEB',
                  color: finalized ? '#059669' : '#D97706',
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {finalized ? '✓ FINALIZED / LOCKED' : '⚡ LIVE DRAFT PREVIEW'}
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B' }}>
              Statutory Payslip for <strong>{MONTHS[month - 1]} {year}</strong> • Days in Month:{' '}
              <strong>{calc.total_days}</strong> • Days Worked:{' '}
              <strong style={{ color: '#059669' }}>{calc.paid_days.toFixed(2)}</strong> • LOP:{' '}
              <strong style={{ color: calc.lop_days > 0 ? '#DC2626' : '#64748B' }}>
                {calc.lop_days}
              </strong>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 22,
              cursor: 'pointer',
              color: '#94A3B8',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748B' }}>
            Fetching statutory breakdown and attendance data…
          </div>
        ) : (
          <>
            {/* Top Control Bar: Monthly CTC & Quick Settings */}
            <div
              style={{
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: 10,
                padding: '12px 16px',
                marginBottom: 18,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                  Monthly CTC (₹):
                </label>
                <input
                  type="number"
                  value={monthlyCtc}
                  onChange={(e) => {
                    setMonthlyCtc(Number(e.target.value));
                    setFinalized(false);
                  }}
                  style={{
                    width: 140,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #CBD5E1',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#1E293B',
                    background: '#ffffff',
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                  LOP / Absent Days:
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={lopDays}
                  onChange={(e) => {
                    setLopDays(Number(e.target.value));
                    setFinalized(false);
                  }}
                  style={{
                    width: 80,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #CBD5E1',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#DC2626',
                    background: '#ffffff',
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={pfApplicable}
                    onChange={(e) => {
                      setPfApplicable(e.target.checked);
                      setFinalized(false);
                    }}
                  />
                  PF Applicable (12%)
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={esicApplicable}
                    onChange={(e) => {
                      setEsicApplicable(e.target.checked);
                      setFinalized(false);
                    }}
                  />
                  ESIC Applicable (0.75%)
                </label>
              </div>
            </div>

            {/* 2-Column TeamNest Statutory Payslip Table */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginBottom: 18,
              }}
            >
              {/* LEFT COLUMN: ALLOWANCES (EARNINGS) */}
              <div
                style={{
                  border: '1px solid #E2E8F0',
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: '#ffffff',
                }}
              >
                <div
                  style={{
                    background: '#F1F5F9',
                    padding: '10px 14px',
                    fontWeight: 700,
                    fontSize: 13,
                    color: '#1E293B',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>ALLOWANCE (EARNED)</span>
                  <span style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>
                    RATE | EARNED
                  </span>
                </div>
                <div style={{ padding: '8px 14px' }}>
                  {/* Basic */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>Basic Salary</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>50% of CTC</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#94A3B8', fontSize: 12, marginRight: 8 }}>
                        ({fmt(calc.basic)})
                      </span>
                      <strong style={{ color: '#0F172A' }}>{fmt(calc.earned_basic)}</strong>
                    </div>
                  </div>

                  {/* HRA */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>House Rent Allowance (HRA)</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>50% of Basic</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#94A3B8', fontSize: 12, marginRight: 8 }}>
                        ({fmt(calc.hra)})
                      </span>
                      <strong style={{ color: '#0F172A' }}>{fmt(calc.earned_hra)}</strong>
                    </div>
                  </div>

                  {/* Statutory Bonus */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>Statutory Bonus</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>8.33% (if Basic ≤ ₹21k)</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#94A3B8', fontSize: 12, marginRight: 8 }}>
                        ({fmt(calc.statutory_bonus)})
                      </span>
                      <strong style={{ color: '#0F172A' }}>{fmt(calc.earned_bonus)}</strong>
                    </div>
                  </div>

                  {/* Special Allowance */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>Special Allowance</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>Balancing Component</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#94A3B8', fontSize: 12, marginRight: 8 }}>
                        ({fmt(calc.special_allowance)})
                      </span>
                      <strong style={{ color: '#0F172A' }}>
                        {fmt(calc.earned_special_allowance)}
                      </strong>
                    </div>
                  </div>

                  {/* Other Earnings (Editable) */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      fontSize: 13,
                    }}
                  >
                    <strong style={{ color: '#334155' }}>Other Earnings / Incentive (₹):</strong>
                    <input
                      type="number"
                      value={otherEarning}
                      onChange={(e) => {
                        setOtherEarning(Number(e.target.value));
                        setFinalized(false);
                      }}
                      style={{
                        width: 100,
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: '1px solid #CBD5E1',
                        fontSize: 13,
                        textAlign: 'right',
                        fontWeight: 700,
                      }}
                    />
                  </div>
                </div>

                {/* Gross Earned Total */}
                <div
                  style={{
                    background: '#F8FAFC',
                    padding: '10px 14px',
                    borderTop: '1px solid #E2E8F0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <strong style={{ fontSize: 13, color: '#1E293B' }}>Gross Salary Earned</strong>
                  <strong style={{ fontSize: 15, color: '#2563EB' }}>
                    {fmt(calc.gross_earned)}
                  </strong>
                </div>
              </div>

              {/* RIGHT COLUMN: DEDUCTIONS */}
              <div
                style={{
                  border: '1px solid #E2E8F0',
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: '#ffffff',
                }}
              >
                <div
                  style={{
                    background: '#F1F5F9',
                    padding: '10px 14px',
                    fontWeight: 700,
                    fontSize: 13,
                    color: '#1E293B',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>DEDUCTIONS</span>
                  <span style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>AMOUNT</span>
                </div>
                <div style={{ padding: '8px 14px' }}>
                  {/* PF Employee */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>Provident Fund (PF)</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>
                        12% of Basic (Max ₹1,800)
                      </div>
                    </div>
                    <strong style={{ color: '#DC2626' }}>{fmt(calc.employee_pf)}</strong>
                  </div>

                  {/* ESIC Employee */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>ESIC (Employee)</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>
                        0.75% (if Basic ≤ ₹21k)
                      </div>
                    </div>
                    <strong style={{ color: '#DC2626' }}>{fmt(calc.employee_esic)}</strong>
                  </div>

                  {/* Salary Advance (Editable) */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ color: '#334155' }}>Salary Advance Recovery (₹):</strong>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>From Advance Balance</div>
                    </div>
                    <input
                      type="number"
                      value={salaryAdvance}
                      onChange={(e) => {
                        setSalaryAdvance(Number(e.target.value));
                        setFinalized(false);
                      }}
                      style={{
                        width: 100,
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: '1px solid #CBD5E1',
                        fontSize: 13,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#DC2626',
                      }}
                    />
                  </div>

                  {/* TDS (Editable) */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: '1px solid #F1F5F9',
                      fontSize: 13,
                    }}
                  >
                    <strong style={{ color: '#334155' }}>T.D.S. / Income Tax (₹):</strong>
                    <input
                      type="number"
                      value={tds}
                      onChange={(e) => {
                        setTds(Number(e.target.value));
                        setFinalized(false);
                      }}
                      style={{
                        width: 100,
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: '1px solid #CBD5E1',
                        fontSize: 13,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#DC2626',
                      }}
                    />
                  </div>

                  {/* Loan / Other Deductions (Editable) */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '7px 0',
                      fontSize: 13,
                    }}
                  >
                    <strong style={{ color: '#334155' }}>Loan / Other Deductions (₹):</strong>
                    <input
                      type="number"
                      value={loanRecovery}
                      onChange={(e) => {
                        setLoanRecovery(Number(e.target.value));
                        setFinalized(false);
                      }}
                      style={{
                        width: 100,
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: '1px solid #CBD5E1',
                        fontSize: 13,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#DC2626',
                      }}
                    />
                  </div>
                </div>

                {/* Gross Deductions Total */}
                <div
                  style={{
                    background: '#F8FAFC',
                    padding: '10px 14px',
                    borderTop: '1px solid #E2E8F0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <strong style={{ fontSize: 13, color: '#1E293B' }}>Gross Deductions</strong>
                  <strong style={{ fontSize: 15, color: '#DC2626' }}>
                    -{fmt(calc.total_deductions)}
                  </strong>
                </div>
              </div>
            </div>

            {/* Net Salary Highlight Banner */}
            <div
              style={{
                background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                borderRadius: 12,
                padding: '16px 20px',
                color: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#94A3B8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 700,
                  }}
                >
                  Net Take Home Salary (Payout)
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#34D399', marginTop: 2 }}>
                  {fmt(calc.final_take_home)}
                </div>
                <div style={{ fontSize: 11.5, color: '#CBD5E1', marginTop: 4 }}>
                  In Words: <em>{numberToWordsINR(calc.final_take_home)}</em>
                </div>
              </div>

              {/* Reimbursements add-on if any */}
              <div style={{ textAlign: 'right' }}>
                <label style={{ fontSize: 11, color: '#94A3B8', display: 'block' }}>
                  + Approved Reimbursement (₹):
                </label>
                <input
                  type="number"
                  value={reimbursement}
                  onChange={(e) => {
                    setReimbursement(Number(e.target.value));
                    setFinalized(false);
                  }}
                  style={{
                    width: 110,
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: '1px solid #475569',
                    background: '#1E293B',
                    color: '#38BDF8',
                    fontSize: 13,
                    textAlign: 'right',
                    fontWeight: 700,
                    marginTop: 4,
                  }}
                />
              </div>
            </div>

            {/* Bottom Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid #E2E8F0',
                paddingTop: 16,
              }}
            >
              <div style={{ fontSize: 12, color: '#64748B' }}>
                {finalized ? (
                  <span style={{ color: '#059669', fontWeight: 700 }}>
                    ✓ Locked & Saved in Database. You can export now.
                  </span>
                ) : (
                  <span>
                    💡 You can modify any number above. Click <strong>Finalize & Save</strong> to lock the slip.
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <S.Btn ghost onClick={onClose}>
                  Close
                </S.Btn>
                <S.Btn ghost onClick={handleExportCsv}>
                  Export CSV
                </S.Btn>
                <S.Btn
                  onClick={handleFinalize}
                  disabled={saving}
                  style={{
                    background: finalized ? '#059669' : '#2563EB',
                    borderColor: finalized ? '#059669' : '#2563EB',
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                >
                  {saving ? 'Finalizing…' : finalized ? '✓ Re-Finalize & Save' : 'Finalize & Save'}
                </S.Btn>
              </div>
            </div>
          </>
        )}
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

export default PayslipReviewModal;
